// Live tracking for the manager's view. Loads the linked-users snapshot
// (GET live-tracking/linked-users), then keeps it live over the
// /live-tracking socket:
//   1. connect with the company token and emit `live-tracking:subscribe` —
//      the server registers this socket for the linked users and acks a fresh
//      snapshot (closing any gap since the REST fetch). Re-sent on every
//      reconnect, so a dropped connection resumes cleanly.
//   2. `location:updated` / `attendance:updated` patch the matching user.
//   3. requestLocations() asks every linked user's app for its current
//      location (`location:request`); the fixes arrive as `location:updated`.
// A token the server refuses (expired) triggers one refetch of the snapshot:
// its 401 runs the shared refresh in axiosBaseQuery, the new authToken lands
// in the store, and this effect reconnects with it.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Socket } from 'socket.io-client';
import { useAppSelector } from '@/store/hooks';
import {
  applyAttendanceUpdate,
  applyLocationUpdate,
  toLinkedUser,
  useGetLinkedUsersQuery,
  type ApiLinkedUser,
  type AttendanceUpdatedEvent,
  type LinkedUser,
  type LocationUpdatedEvent,
} from '@/api/endpoints/liveTrackingApi';
import {
  LiveTrackingEvents,
  connectLiveTracking,
  isAuthRejection,
  type SocketAck,
} from '@/api/liveTrackingSocket';

export interface LiveTrackingState {
  users: LinkedUser[];
  /** Socket connected and subscribed — updates are flowing. */
  isLive: boolean;
  isLoading: boolean;
  isError: boolean;
  /** Epoch ms of the latest snapshot or event. */
  lastUpdateAt: number | null;
  /** Ask every linked user's app for its current location; resolves with how
   *  many users were asked, rejects with the server's reason. */
  requestLocations: () => Promise<number>;
}

export const useLiveTracking = (): LiveTrackingState => {
  const authToken = useAppSelector((state) => state.auth.authToken);
  const { data, isLoading, isError, refetch } = useGetLinkedUsersQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const [users, setUsers] = useState<LinkedUser[]>([]);
  const [isLive, setIsLive] = useState(false);
  const [lastUpdateAt, setLastUpdateAt] = useState<number | null>(null);

  // Kept in refs so the socket effect only re-runs when the token changes.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  const rejectedTokenRef = useRef<string | null>(null);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!data) return;
    setUsers(data);
    setLastUpdateAt(Date.now());
  }, [data]);

  useEffect(() => {
    if (!authToken) return undefined;
    const socket = connectLiveTracking(authToken);
    socketRef.current = socket;

    const patchUser = (companyUserId: string, patch: (user: LinkedUser) => LinkedUser) => {
      setUsers((prev) => prev.map((u) => (u.companyUserId === companyUserId ? patch(u) : u)));
      setLastUpdateAt(Date.now());
    };

    socket.on('connect', () => {
      socket.emit(LiveTrackingEvents.Subscribe, (ack: SocketAck<ApiLinkedUser[]>) => {
        if (!ack?.success || !ack.data) return;
        setUsers(ack.data.map(toLinkedUser));
        setLastUpdateAt(Date.now());
        setIsLive(true);
      });
    });
    socket.on('disconnect', () => setIsLive(false));
    socket.on('connect_error', (error: Error) => {
      setIsLive(false);
      // Refresh at most once per refused token, so a session the server keeps
      // rejecting can't loop.
      if (isAuthRejection(error) && rejectedTokenRef.current !== authToken) {
        rejectedTokenRef.current = authToken;
        void refetchRef.current();
      }
    });
    socket.on(LiveTrackingEvents.LocationUpdated, (event: LocationUpdatedEvent) =>
      patchUser(event.companyUserId, (user) => applyLocationUpdate(user, event)),
    );
    socket.on(LiveTrackingEvents.AttendanceUpdated, (event: AttendanceUpdatedEvent) =>
      patchUser(event.companyUserId, (user) => applyAttendanceUpdate(user, event)),
    );

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
      setIsLive(false);
    };
  }, [authToken]);

  const requestLocations = useCallback(
    () =>
      new Promise<number>((resolve, reject) => {
        const socket = socketRef.current;
        if (!socket) {
          reject(new Error('Live tracking is not connected'));
          return;
        }
        socket.emit(
          LiveTrackingEvents.RequestLocations,
          (ack: SocketAck<{ requested: number }>) => {
            if (ack?.success) resolve(ack.data?.requested ?? 0);
            else reject(new Error(ack?.error?.msg ?? ack?.message ?? 'Request failed'));
          },
        );
      }),
    [],
  );

  return { users, isLive, isLoading, isError, lastUpdateAt, requestLocations };
};
